import { ConductorAllChildrenActive, Router } from "@frui.ts/screens/dist";
import { computed, observable } from "mobx";
import LocalizationService from "services/localizationService";
import OrderSummaryViewModel from "./orderSummaryViewModel";
import deburr from "lodash/deburr";
import { interfaces } from "inversify";
import SummaryViewModel from "../../multiEshop/summaryMultiEshopViewModel";
import NotificationService from "../../../services/notificationService";
import GaService from "../../../services/gaService";
import { utcToCZZonedTime } from "utils/dateUtils";
import ChargeOrder from "models/chargeOrder";
import ChargeOrderItem from "models/chargeOrderItem";
import CustomerReviewViewModel from "viewModels/customerReview/customerReviewViewModel";
import EventBus, {
  customerRatingOrderData,
  paymentConfirmationAvailable,
  paymentConfirmationError,
} from "../../../services/eventBus";

export const BankTransferNavigationName = "bank_transfer";

export enum PaymentInstructionType {
  Domestic = "domestic",
  Sepa = "sepa",
}

@Router.registerRoute({ route: `${BankTransferNavigationName}/:orderId`, name: Router.Self })
export default class BankTransferViewModel extends ConductorAllChildrenActive<
  SummaryViewModel | OrderSummaryViewModel | CustomerReviewViewModel
> {
  private dateRegex = /%DATE%/g;
  @observable selected: ChargeOrder;
  @observable selectedPaymentType: PaymentInstructionType;

  constructor(
    public chargeOrder: ChargeOrder,
    public localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected gaService: GaService,
    orderSummaryViewModelFactory: ReturnType<typeof OrderSummaryViewModel.Factory>,
    summaryVMFactory: ReturnType<typeof SummaryViewModel.Factory>,
    public customerReviewViewModel: CustomerReviewViewModel,
    private eventBus: EventBus
  ) {
    super();
    this.navigationName = `${BankTransferNavigationName}/${this.chargeOrder.id}`;
    this.notificationService.notifications.clear();
    this.eventBus.publish(customerRatingOrderData({ orderId: this.chargeOrder.id }));
    this.selectedPaymentType = this.domesticPayment ? PaymentInstructionType.Domestic : PaymentInstructionType.Sepa;

    if (this.moreThanOneLicensePlateOrder) {
      this.children.push(summaryVMFactory(false, false, undefined, undefined, this.chargeOrder));
    } else {
      this.children.push(orderSummaryViewModelFactory(this.chargeOrder));
    }
    this.children.push(customerReviewViewModel);
  }

  @computed
  get isMultiEshopOrder() {
    return this.chargeOrder._origin === "multiple";
  }

  /* DomesticPayment */
  @computed
  get accountNumber() {
    return this.domesticPayment?.bankAccount?.accountNumber;
  }

  @computed
  get messageForRecipient() {
    return this.domesticPayment?.messageForRecipient;
  }

  @computed
  get bankCode() {
    return this.domesticPayment?.bankAccount?.bankCode;
  }

  /* SepaPayment */

  @computed
  get iban() {
    return this.sepaPayment?.iban;
  }

  @computed
  get counterpartyName() {
    return this.sepaPayment?.counterpartyName;
  }

  @computed
  get additionalInformation() {
    return this.sepaPayment?.additionalInformation;
  }

  @computed
  get swiftCode() {
    return this.sepaPayment?.swiftCode;
  }

  /* Shared */
  @computed
  get currencyCodeDomestic() {
    return this.domesticPayment?.currencyCode;
  }
  @computed
  get currencyCodeSepa() {
    return this.sepaPayment?.currencyCode;
  }

  // Computed properties pro aktivní instrukce podle vybraného typu
  @computed
  get activePaymentInstructions() {
    return this.selectedPaymentType === PaymentInstructionType.Domestic ? this.domesticPayment : this.sepaPayment;
  }

  @computed
  get activeCurrencyCode() {
    return this.selectedPaymentType === PaymentInstructionType.Domestic ? this.currencyCodeDomestic : this.currencyCodeSepa;
  }

  @computed
  get activePrice() {
    return this.selectedPaymentType === PaymentInstructionType.Domestic ? this.priceDomestic : this.priceSepa;
  }

  @computed
  get paymentTypeOptions() {
    const options = [];
    if (this.domesticPayment) {
      options.push({
        id: PaymentInstructionType.Domestic,
        name: "general.payment.domestic",
        type: PaymentInstructionType.Domestic,
      });
    }
    if (this.sepaPayment) {
      options.push({
        id: PaymentInstructionType.Sepa,
        name: "general.payment.sepa",
        type: PaymentInstructionType.Sepa,
      });
    }
    return options;
  }

  @computed
  get variableSymbol() {
    return this.chargeOrder?.transferPaymentInstructions?.variableSymbol;
  }

  @computed
  get qrcode() {
    // Načítáme QR kód podle vybraného typu platby
    const activePayment = this.selectedPaymentType === PaymentInstructionType.Domestic ? this.domesticPayment : this.sepaPayment;
    return activePayment?.qrCode;
  }

  @computed
  get priceDomestic() {
    if (!this.domesticPayment?.amount) return undefined;

    return this.localizationService.formatPrice(this.domesticPayment.amount, this.currencyCodeDomestic);
  }

  @computed
  get priceSepa() {
    if (!this.sepaPayment?.amount) return undefined;

    return this.localizationService.formatPrice(this.sepaPayment?.amount, this.currencyCodeSepa);
  }

  @computed
  get isSepaPayment() {
    return (
      this.chargeOrder?.transferPaymentInstructions?.sepaPayment &&
      !this.chargeOrder?.transferPaymentInstructions?.domesticPayment
    );
  }
  @computed
  get sepaPayment() {
    return this.chargeOrder?.transferPaymentInstructions?.sepaPayment;
  }

  @computed
  get domesticPayment() {
    return this.chargeOrder?.transferPaymentInstructions?.domesticPayment;
  }

  @computed
  get transferPaymentInstruction() {
    return this.isSepaPayment ? this.sepaPayment : this.domesticPayment;
  }

  @computed
  get moreThanOneLicensePlateOrder() {
    return (
      this.chargeOrder.charges.reduce(
        (summary: number, item: ChargeOrderItem) =>
          summary + (item.licensePlate ? 1 : 0) + (item.licensePlates ? item.licensePlates.length : 0),
        0
      ) > 1
    );
  }

  @computed
  get bankTransferInfo() {
    if (!this.chargeOrder || !this.chargeOrder?.transferPaymentInstructions?.dueDate) return undefined;

    const czechDueDate = utcToCZZonedTime(new Date(this.chargeOrder?.transferPaymentInstructions?.dueDate));

    return this.localizationService
      .translateGeneral("eshop.transfer.warning")
      .replace(this.dateRegex, this.localizationService.formatDate(czechDueDate));
  }

  navigate(subPath: string | undefined, params: any): Promise<void> {
    this.gaService.sendOrderInfo(this.chargeOrder);
    return super.navigate(subPath, params);
  }

  translate(code: string) {
    return this.localizationService.translateGeneral(`eshop.transfer.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrder: ChargeOrder) => {
      return new BankTransferViewModel(
        chargeOrder,
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(GaService),
        container.get(OrderSummaryViewModel.Factory),
        container.get(SummaryViewModel.Factory),
        container.get(CustomerReviewViewModel),
        container.get(EventBus)
      );
    };
  }
}
